import React, { useState, useMemo } from 'react';
import { VideoRequest } from '../types/video';
import SkeletonCard from './SkeletonCard';

interface HistoryPanelProps {
  history: VideoRequest[];
  onSelectVideo: (video: VideoRequest) => void;
  isLoading: boolean;
}

type FilterStatus = 'all' | 'completed' | 'processing' | 'failed';

const HistoryPanel: React.FC<HistoryPanelProps> = ({
  history,
  onSelectVideo,
  isLoading,
}) => {
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return 'Zojuist';
    if (diffInMinutes < 60) return `${diffInMinutes} min geleden`;
    if (diffInHours < 24) return `${diffInHours} uur geleden`;
    if (diffInDays === 1) return 'Gisteren';
    if (diffInDays < 7) return `${diffInDays} dagen geleden`;
    return date.toLocaleDateString('nl-NL');
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed': return 'text-brand-green';
      case 'processing': return 'text-brand-yellow';
      case 'failed': return 'text-brand-red';
      case 'pending': return 'text-text-secondary';
      default: return 'text-text-secondary';
    }
  };

  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Filter history based on selected status
  const filteredHistory = useMemo(() => {
    if (filterStatus === 'all') return history;
    return history.filter(video => video.status === filterStatus);
  }, [history, filterStatus]);

  if (isLoading) {
    return (
      <div className="panel p-6">
        <div className="flex items-center mb-4">
          <span className="text-xl mr-2">📚</span>
          <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">Geschiedenis</h2>
        </div>
        <div className="space-y-3">
          <SkeletonCard count={3} />
        </div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="panel p-6">
        <div className="flex items-center mb-4">
          <span className="text-xl mr-2">📚</span>
          <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">Geschiedenis</h2>
        </div>
        <div className="text-center py-8">
          <div className="text-4xl mb-2">📭</div>
          <p className="text-[var(--color-text-secondary)] text-sm">
            Nog geen video's gegenereerd
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="panel p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <span className="text-xl mr-2">📚</span>
          <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">Geschiedenis</h2>
        </div>
        <span className="text-xs text-[var(--color-text-secondary)]">
          {filteredHistory.length} van {history.length} video{history.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Filter buttons */}
      <div className="flex space-x-2 mb-4">
        <button
          onClick={() => setFilterStatus('all')}
          className={`px-3 py-1 text-xs rounded-full transition-colors ${
            filterStatus === 'all'
              ? 'bg-accent text-white shadow-sm'
              : 'bg-[var(--color-surface-alt)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
          }`}
        >
          Alle
        </button>
        <button
          onClick={() => setFilterStatus('completed')}
          className={`px-3 py-1 text-xs rounded-full transition-colors ${
            filterStatus === 'completed'
              ? 'bg-brand-green text-white shadow-sm'
              : 'bg-[var(--color-surface-alt)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
          }`}
        >
          Voltooid
        </button>
        <button
          onClick={() => setFilterStatus('processing')}
          className={`px-3 py-1 text-xs rounded-full transition-colors ${
            filterStatus === 'processing'
              ? 'bg-brand-yellow text-text-primary shadow-sm'
              : 'bg-[var(--color-surface-alt)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
          }`}
        >
          Bezig
        </button>
        <button
          onClick={() => setFilterStatus('failed')}
          className={`px-3 py-1 text-xs rounded-full transition-colors ${
            filterStatus === 'failed'
              ? 'bg-brand-red text-white shadow-sm'
              : 'bg-[var(--color-surface-alt)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
          }`}
        >
          Mislukt
        </button>
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {filteredHistory.map((video) => (
          <div
            key={video.id}
            onClick={() => onSelectVideo(video)}
            className="p-3 bg-[var(--color-surface-alt)] border border-[var(--color-border)] rounded-lg
                     hover:border-accent cursor-pointer transition-all duration-200
                     hover:bg-white"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${getStatusColor(video.status)}`}>
                  {video.status === 'pending' && 'Wachten...'}
                  {video.status === 'processing' && 'Bezig met genereren...'}
                  {video.status === 'completed' && 'Voltooid'}
                  {video.status === 'failed' && 'Mislukt'}
                </div>
                <div className="text-xs text-[var(--color-text-secondary)] mt-1">
                  {formatTimeAgo(video.createdAt)}
                </div>
              </div>
              {video.actualCost && (
                <span className="text-xs text-accent font-medium ml-2">
                  ${video.actualCost.toFixed(2)}
                </span>
              )}
            </div>

            <div className="text-sm text-[var(--color-text-primary)] line-clamp-2">
              {truncateText(video.scenarioDescription, 80)}
            </div>

            {/* Settings summary */}
            <div className="flex items-center space-x-2 mt-2 text-xs text-[var(--color-text-secondary)] flex-wrap gap-y-1">
              {video.provider && (
                <>
                  <span>{video.provider.toUpperCase()}</span>
                  <span>•</span>
                </>
              )}
              <span>Camera: {video.settings.cameraAngle}</span>
              <span>•</span>
              <span>Weer: {video.settings.weather}</span>
              <span>•</span>
              <span>Duur: {video.settings.durationSeconds}s</span>
            </div>

            {/* Progress bar for processing videos */}
            {video.status === 'processing' && (
              <div className="mt-2">
                <div className="progress-bar">
                  <div
                    className="progress-fill animate-pulse"
                    style={{ width: `${video.progressPercentage ?? 0}%` }}
                  ></div>
                </div>
                <div className="text-xs text-[var(--color-text-secondary)] mt-1">
                  {video.progressPercentage ?? 0}% voltooid
                </div>
              </div>
            )}

            {/* Error message for failed videos */}
            {video.status === 'failed' && video.errorMessage && (
              <div className="mt-2 text-xs text-brand-red">
                Fout: {video.errorMessage}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Clear history button */}
      {history.length > 0 && (
        <button className="w-full mt-4 py-2 text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors">
          Geschiedenis wissen
        </button>
      )}
    </div>
  );
};

export default HistoryPanel;
